import { useState, useEffect, useCallback } from 'react';
import { getAllPendingMedia, getAllApprovedMedia, approveAllPendingForCustomer, deleteMediaRecord } from '../lib/storage';

const B = {
  green: '#95C11F', greenLight: '#edf7d6', greenDark: '#5a7a0a',
  blue:  '#36A9E1', blueLight:  '#d6effa', blueDark:  '#1a7aab',
  amber: '#ba7517', amberLight: '#faeeda',
  red:   '#a32d2d',
};

function Lightbox({ photos, startIdx, onClose, onDelete }) {
  const [idx, setIdx] = useState(startIdx);
  const p = photos[idx];

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowRight')  setIdx(i => Math.min(i + 1, photos.length - 1));
      if (e.key === 'ArrowLeft')   setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [photos, onClose]);

  if (!p) return null;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.93)', zIndex:1000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ position:'relative', display:'flex', flexDirection:'column', alignItems:'center', gap:12, maxWidth:'90vw' }}>
        {p.file_type === 'video'
          ? <video src={p.url} controls style={{ maxWidth:'90vw', maxHeight:'65vh', borderRadius:8 }}/>
          : <img src={p.url} alt={p.file_name} style={{ maxWidth:'90vw', maxHeight:'65vh', borderRadius:8, objectFit:'contain' }}/>
        }
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <button onClick={() => setIdx(i => Math.max(i-1,0))} disabled={idx===0}
            style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:8, padding:'6px 14px', cursor:idx===0?'default':'pointer', fontSize:18 }}>‹</button>
          <div style={{ textAlign:'center' }}>
            <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.6)', fontFamily:'Montserrat,sans-serif' }}>{idx+1} / {photos.length}</p>
            <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,0.4)', fontFamily:'Montserrat,sans-serif' }}>{p.visit_date} · {p.uploaded_by}</p>
          </div>
          <button onClick={() => setIdx(i => Math.min(i+1,photos.length-1))} disabled={idx===photos.length-1}
            style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:8, padding:'6px 14px', cursor:idx===photos.length-1?'default':'pointer', fontSize:18 }}>›</button>
          <button onClick={() => onDelete(p)}
            style={{ background:'rgba(163,45,45,0.85)', border:'none', color:'#fff', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:12, fontFamily:'Montserrat,sans-serif', fontWeight:700 }}>Delete</button>
        </div>
        <button onClick={onClose} style={{ position:'absolute', top:-40, right:0, background:'none', border:'none', color:'#fff', fontSize:24, cursor:'pointer' }}>✕</button>
      </div>
    </div>
  );
}

function MediaThumb({ item, onClick, onDelete }) {
  return (
    <div onClick={onClick} style={{ position:'relative', aspectRatio:'1', borderRadius:8, overflow:'hidden', border:`1.5px solid ${B.amber}66`, cursor:'pointer' }}>
      {item.file_type === 'video'
        ? <div style={{ width:'100%', height:'100%', background:'#1a1a1a', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:22 }}>▶</div>
        : item.url
          ? <img src={item.url} alt={item.file_name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          : <div style={{ width:'100%', height:'100%', background:'#222' }}/>
      }
      <button onClick={e => { e.stopPropagation(); onDelete(item); }}
        style={{ position:'absolute', top:3, right:3, width:18, height:18, borderRadius:'50%', background:'rgba(163,45,45,0.85)', color:'#fff', border:'none', fontSize:10, cursor:'pointer' }}>✕</button>
    </div>
  );
}

export default function AdminMediaReview({ t }) {
  const [pendingMedia,  setPending]    = useState([]);
  const [approvedMedia, setApproved]   = useState([]);
  const [loading,       setLoading]    = useState(true);
  const [error,         setError]      = useState(null);
  const [lightbox,      setLightbox]   = useState(null);
  const [processing,    setProcessing] = useState({});

  const loadMedia = useCallback(async () => {
    setLoading(true);
    const [pendingRes, approvedRes] = await Promise.all([getAllPendingMedia(), getAllApprovedMedia()]);
    if (pendingRes.error)  setError('Failed to load pending media.');
    if (approvedRes.error) setError('Failed to load approved media.');
    setPending(pendingRes.media);
    setApproved(approvedRes.media);
    setLoading(false);
  }, []);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const pendingByCustomer = pendingMedia.reduce((acc, item) => {
    const id = item.customer_id;
    if (!acc[id]) acc[id] = { customer: item.customers, items: [] };
    acc[id].items.push(item);
    return acc;
  }, {});

  const handleApproveAll = async (customerId) => {
    setProcessing(p => ({ ...p, [customerId]: true }));
    const { error } = await approveAllPendingForCustomer(customerId);
    if (!error) await loadMedia();
    else setError(`Approve failed: ${error.message}`);
    setProcessing(p => ({ ...p, [customerId]: false }));
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.file_name}? This cannot be undone.`)) return;
    setProcessing(p => ({ ...p, [item.id]: true }));
    const { error } = await deleteMediaRecord(item.id, item.storage_path);
    if (!error) { await loadMedia(); if (lightbox) setLightbox(null); }
    else setError(`Delete failed: ${error.message}`);
    setProcessing(p => ({ ...p, [item.id]: false }));
  };

  if (loading) return (
    <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
      <div style={{ width:40, height:40, border:`3px solid ${B.green}`, borderTopColor:'transparent', borderRadius:'50%', margin:'0 auto 14px', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ fontSize:13, color:t.textSec, fontFamily:'Montserrat,sans-serif' }}>Loading media…</p>
    </div>
  );

  const totalPending = pendingMedia.length;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {lightbox && <Lightbox photos={lightbox.photos} startIdx={lightbox.idx} onClose={() => setLightbox(null)} onDelete={handleDelete}/>}

      {error && (
        <div style={{ background:'#3a0f0f', border:`0.5px solid ${B.red}44`, borderRadius:10, padding:'10px 14px' }}>
          <p style={{ margin:0, fontSize:12, color:'#f87171', fontFamily:'Montserrat,sans-serif' }}>{error}</p>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div style={{ background:totalPending>0?B.amberLight:t.bgCard, border:`0.5px solid ${t.border}`, borderRadius:10, padding:'0.75rem 1rem' }}>
          <p style={{ margin:0, fontSize:10, fontWeight:700, color:totalPending>0?B.amber:t.textSec, fontFamily:'Montserrat,sans-serif' }}>Pending review</p>
          <p style={{ margin:'2px 0 0', fontSize:24, fontWeight:700, color:totalPending>0?B.amber:t.text, fontFamily:'Montserrat,sans-serif' }}>{totalPending}</p>
        </div>
        <div style={{ background:t.bgCard, border:`0.5px solid ${t.border}`, borderRadius:10, padding:'0.75rem 1rem' }}>
          <p style={{ margin:0, fontSize:10, fontWeight:700, color:t.textSec, fontFamily:'Montserrat,sans-serif' }}>Published</p>
          <p style={{ margin:'2px 0 0', fontSize:24, fontWeight:700, color:t.text, fontFamily:'Montserrat,sans-serif' }}>{approvedMedia.length}</p>
        </div>
      </div>

      {Object.keys(pendingByCustomer).length > 0 && (
        <div>
          <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:14, color:t.text, fontFamily:'Montserrat,sans-serif' }}>Pending review</p>
          {Object.entries(pendingByCustomer).map(([custId, { customer, items }]) => (
            <div key={custId} style={{ background:t.bgCard, border:`0.5px solid ${t.border}`, borderRadius:14, padding:'1rem 1.25rem', marginBottom:12, boxShadow:t.shadow }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <div>
                  <p style={{ margin:0, fontWeight:700, fontSize:13, color:t.text, fontFamily:'Montserrat,sans-serif' }}>{customer?.name || 'Customer'}</p>
                  <p style={{ margin:'1px 0 0', fontSize:11, color:t.textSec, fontFamily:'Montserrat,sans-serif' }}>{customer?.address} · {items.length} file{items.length>1?'s':''}</p>
                </div>
                <button onClick={() => handleApproveAll(custId)} disabled={processing[custId]}
                  style={{ fontSize:11, padding:'5px 12px', borderRadius:6, background:processing[custId]?'#aaa':B.green, color:'#fff', border:'none', cursor:processing[custId]?'default':'pointer', fontFamily:'Montserrat,sans-serif', fontWeight:700 }}>
                  {processing[custId] ? '…' : `Approve all (${items.length})`}
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:10 }}>
                {items.map((item, fi) => (
                  <MediaThumb key={item.id} item={item} onClick={() => setLightbox({ photos:items, idx:fi })} onDelete={handleDelete}/>
                ))}
              </div>
              <button onClick={() => handleApproveAll(custId)} disabled={processing[custId]}
                style={{ width:'100%', padding:'9px 0', borderRadius:8, background:'transparent', border:`0.5px solid ${B.green}`, color:B.green, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'Montserrat,sans-serif' }}>
                Publish all to customer
              </button>
            </div>
          ))}
        </div>
      )}

      {approvedMedia.length > 0 && (
        <div>
          <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:14, color:t.text, fontFamily:'Montserrat,sans-serif' }}>Published to customers</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
            {approvedMedia.map((item, fi) => (
              <div key={item.id}>
                <MediaThumb item={item} onClick={() => setLightbox({ photos:approvedMedia, idx:fi })} onDelete={handleDelete}/>
                <p style={{ margin:'3px 0 0', fontSize:9, color:t.textHint, fontFamily:'Montserrat,sans-serif', textAlign:'center', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {item.customers?.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingMedia.length === 0 && approvedMedia.length === 0 && (
        <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
          <p style={{ fontSize:32, marginBottom:10 }}>📷</p>
          <p style={{ fontWeight:700, fontSize:15, color:t.text, fontFamily:'Montserrat,sans-serif' }}>No media yet</p>
          <p style={{ fontSize:13, color:t.textSec, fontFamily:'Montserrat,sans-serif' }}>Photos and videos appear here after staff complete route stops.</p>
        </div>
      )}

      <button onClick={loadMedia} style={{ padding:'10px 0', borderRadius:10, border:`0.5px solid ${t.border}`, background:'transparent', color:t.textSec, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'Montserrat,sans-serif' }}>
        Refresh ↺
      </button>
    </div>
  );
}
